import { useState, useEffect } from "react";
import { supabase } from "../supabase/supabaseClient";

export type UserRole = "initial" | "user" | "disapproved" | "admin";

interface UserRoleData {
  role: UserRole | null;
  isLoading: boolean;
  error: string | null;
  isInitial: boolean;
  isUser: boolean;
  isDisapproved: boolean;
  isAdmin: boolean;
  canAccessApp: boolean;
  needsApproval: boolean;
}

export const useUserRole = (): UserRoleData => {
  const [role, setRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only run initial check if we haven't initialized yet
    if (!hasInitialized) {
      checkUserRole();
    }

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        checkUserRole();
      } else if (event === "SIGNED_OUT") {
        setRole(null);
        setIsLoading(false);
        setError(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [hasInitialized]);

  const checkUserRole = async () => {
    try {
      // Only set loading to true if we haven't initialized yet
      if (!hasInitialized) {
        setIsLoading(true);
      }
      setError(null);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setRole(null);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from("base_profiles")
        .select("role")
        .eq("id", user.id)
        .single();

      if (profileError) {
        throw profileError;
      }

      setRole(profile?.role || "initial");
    } catch (error) {
      console.error("Error checking user role:", error);
      setError(
        error instanceof Error ? error.message : "Failed to check user role"
      );
      setRole(null);
    } finally {
      setIsLoading(false);
      setHasInitialized(true);
    }
  };

  // Check if signup blocking is bypassed via environment variable
  const isSignupBlockBypassed = (): boolean => {
    return import.meta.env.VITE_BYPASS_SIGNUP_BLOCK === "true";
  };

  // Derived states
  const isInitial = role === "initial";
  const isUser = role === "user";
  const isDisapproved = role === "disapproved";
  const isAdmin = role === "admin";

  // User can access the app if:
  // 1. They are approved users or admins
  // 2. OR they are initial users but signup blocking is bypassed
  const canAccessApp =
    isUser || isAdmin || (isInitial && isSignupBlockBypassed());

  // User needs approval if they are initial and signup blocking is not bypassed
  const needsApproval = isInitial && !isSignupBlockBypassed();

  return {
    role,
    isLoading,
    error,
    isInitial,
    isUser,
    isDisapproved,
    isAdmin,
    canAccessApp,
    needsApproval,
  };
};
